import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "message-bubble" }
const _hoisted_2 = {
  key: 0,
  class: "structured-response"
}
const _hoisted_3 = { class: "review-section" }
const _hoisted_4 = ["innerHTML"]
const _hoisted_5 = {
  key: 0,
  class: "comment-section"
}
const _hoisted_6 = { class: "comment-content" }
const _hoisted_7 = ["innerHTML"]
const _hoisted_8 = { class: "message-meta" }
const _hoisted_9 = { class: "timestamp" }
const _hoisted_10 = {
  key: 0,
  class: "stats"
}
const _hoisted_11 = { class: "message-actions" }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = ["onClick"]
const _hoisted_14 = {
  key: 2,
  class: "winner-selected",
  disabled: "",
  title: "Valittu viimeisteltäväksi"
}
const _hoisted_15 = ["onClick", "title"]
const _hoisted_16 = { key: 0 }
const _hoisted_17 = { key: 1 }

import { ref, watch, nextTick } from "vue";
import { marked } from "marked";
import type { Message } from "../types/chat";

/**
 * MessageList is a presentational component responsible for rendering
 * a list of chat messages.
 */


export default /*@__PURE__*/_defineComponent({
  __name: 'MessageList',
  props: {
    messages: {},
    canSelectWinner: { type: Boolean },
    selectedWinnerId: {}
  },
  emits: ["selectWinner"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const messageListRef = ref<HTMLElement | null>(null);
const copiedMessageId = ref<string | null>(null);

const formatDate = (date: Date): string => {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatStats = (stats: {
  wordCount: number;
  characterCount: number;
  characterCountNoSpaces: number;
}) => {
  return `${stats.wordCount} sanaa · ${stats.characterCount} merkkiä`;
};

// Function to render AI responses as markdown with fallback to plain text
const renderMessageContent = (message: Message): string => {
  // For user messages, return as-is (they're already HTML)
  if (message.sender === "user") {
    return message.content;
  }

  // For AI messages, try to render as markdown
  try {
    // Check if content already contains HTML tags (already processed)
    if (
      message.content.includes('<div class="loading-spinner">') ||
      message.content.includes("<p>") ||
      message.content.includes("<div>")
    ) {
      return message.content;
    }

    // Try to parse as markdown
    return marked(message.content);
  } catch (error) {
    console.warn(
      "Failed to parse markdown, falling back to plain text:",
      error,
    );
    // Fallback: wrap in paragraph tag for consistent styling
    return `<p>${message.content}</p>`;
  }
};

// Function to extract plain text from message content
const getPlainTextContent = (message: Message): string => {
  // For loading messages, return a simple message
  if (message.content.includes('<div class="loading-spinner">')) {
    return "Ladataan...";
  }

  // Create a temporary div to extract plain text from HTML/markdown
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = renderMessageContent(message);
  return tempDiv.textContent || tempDiv.innerText || message.content;
};

// Function to select a message as winner for final editing
const selectWinner = (message: Message) => {
  // Prefer the structured review field (clean markdown) over message.content,
  // which for older messages without structuredResponse may not be reliable.
  const contentToEdit = message.structuredResponse?.review || message.content;
  emit("selectWinner", message.id, contentToEdit);
};

// Function to copy message content to clipboard
const copyMessage = async (message: Message) => {
  try {
    const plainText = getPlainTextContent(message);
    await navigator.clipboard.writeText(plainText);

    // Show visual feedback
    copiedMessageId.value = message.id;
    setTimeout(() => {
      copiedMessageId.value = null;
    }, 2000);
  } catch (error) {
    console.error("Failed to copy message:", error);
    // Fallback for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = getPlainTextContent(message);
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand("copy");
      copiedMessageId.value = message.id;
      setTimeout(() => {
        copiedMessageId.value = null;
      }, 2000);
    } catch (fallbackError) {
      console.error("Fallback copy also failed:", fallbackError);
    }
    document.body.removeChild(textArea);
  }
};

// Function to scroll to the bottom of the message list
const scrollToBottom = () => {
  if (messageListRef.value) {
    messageListRef.value.scrollTop = messageListRef.value.scrollHeight;
  }
};

// Watch for changes in messages and auto-scroll to bottom
watch(
  () => props.messages,
  () => {
    void nextTick(() => {
      scrollToBottom();
    });
  },
  { deep: true },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "message-list",
    ref_key: "messageListRef",
    ref: messageListRef
  }, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.messages, (message) => {
      return (_openBlock(), _createElementBlock("div", {
        key: message.id,
        class: _normalizeClass(["message-container", `sender-${message.sender}`])
      }, [
        _createElementVNode("div", _hoisted_1, [
          (message.sender === 'ai' && message.structuredResponse)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  _createElementVNode("div", {
                    class: "message-content",
                    innerHTML: 
                renderMessageContent({
                  ...message,
                  content: message.structuredResponse.review,
                })
              
                  }, null, 8, _hoisted_4)
                ]),
                (message.structuredResponse.comment.trim())
                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                      _cache[0] || (_cache[0] = _createElementVNode("div", { class: "comment-header" }, "💭 Kommentti opettajalle:", -1)),
                      _createElementVNode("div", _hoisted_6, _toDisplayString(message.structuredResponse.comment), 1)
                    ]))
                  : _createCommentVNode("", true)
              ]))
            : (_openBlock(), _createElementBlock("div", {
                key: 1,
                class: "message-content",
                innerHTML: renderMessageContent(message)
              }, null, 8, _hoisted_7)),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("span", _hoisted_9, _toDisplayString(formatDate(message.timestamp)), 1),
            (message.sender === 'ai' && message.stats)
              ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(formatStats(message.stats)), 1))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", _hoisted_11, [
            (
              message.sender === 'ai' && __props.canSelectWinner && !__props.selectedWinnerId
            )
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  class: "winner-button",
                  onClick: ($event: any) => (selectWinner(message)),
                  title: "Valitse viimeisteltäväksi"
                }, " ✨ Valitse ", 8, _hoisted_12))
              : _createCommentVNode("", true),
            (
              message.sender === 'ai' &&
              __props.canSelectWinner &&
              __props.selectedWinnerId &&
              __props.selectedWinnerId !== message.id
            )
              ? (_openBlock(), _createElementBlock("button", {
                  key: 1,
                  class: "winner-change-button",
                  onClick: ($event: any) => (selectWinner(message)),
                  title: "Vaihda valinta tähän viestiin"
                }, " 🔄 Vaihda ", 8, _hoisted_13))
              : _createCommentVNode("", true),
            (message.sender === 'ai' && __props.selectedWinnerId === message.id)
              ? (_openBlock(), _createElementBlock("button", _hoisted_14, " ✅ Valittu "))
              : _createCommentVNode("", true),
            _createElementVNode("button", {
              class: "copy-button",
              onClick: ($event: any) => (copyMessage(message)),
              title: 
              copiedMessageId.value === message.id ? 'Kopioitu!' : 'Kopioi viesti'
            
            }, [
              (copiedMessageId.value === message.id)
                ? (_openBlock(), _createElementBlock("span", _hoisted_16, "✓"))
                : (_openBlock(), _createElementBlock("span", _hoisted_17, "📋"))
            ], 8, _hoisted_15)
          ])
        ])
      ], 2))
    }), 128))
  ], 512))
}
}

})